import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const ServicesTab = ({
    services,
    onAddService,
    onUpdateService,
    onRemoveService,
    clinicServices,
    readOnly,
    visitDate,
    lengthOfStay
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    // State
    const [activeDay, setActiveDay] = useState(1);
    const [searchQuery, setSearchQuery] = useState(''); // Local search for adding
    const [showSearchRow, setShowSearchRow] = useState(false);

    // Helpers
    const getDayDate = (dayIndex) => {
        if (!visitDate) return null;
        const date = new Date(visitDate);
        date.setDate(date.getDate() + (dayIndex - 1));
        return date.toISOString().split('T')[0];
    };

    const activeDateString = useMemo(() => getDayDate(activeDay), [activeDay, visitDate]);

    // Lock editing only if the specific active day has passed (real calendar day)
    const dayLocked = useMemo(() => {
        if (!activeDateString) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const currentDayDate = new Date(activeDateString);
        currentDayDate.setHours(0, 0, 0, 0);

        // If the day is in the past, it's locked. Today and future are editable.
        return currentDayDate < today;
    }, [activeDateString]);

    // While on admission day, all days (1..N) are editable. After that, everything is read-only.
    const isReadOnly = readOnly || dayLocked;

    // Filter services for current day
    const dayServices = useMemo(() => {
        return services.filter(s => s.due_date === activeDateString);
    }, [services, activeDateString]);

    // Search Logic (Starts With)
    const filteredSearchOptions = useMemo(() => {
        if (!searchQuery) return [];
        const lowerQ = searchQuery.toLowerCase();
        return (clinicServices || []).filter(s => {
            const nameEn = s.service_name_en?.toLowerCase() || '';
            const nameAr = s.service_name_ar?.toLowerCase() || '';
            return nameEn.startsWith(lowerQ) || nameAr.startsWith(lowerQ);
        }).slice(0, 10); // Limit results
    }, [searchQuery, clinicServices]);

    const handleSelectService = (service) => {
        if (isReadOnly) return;
        onAddService(service, { due_date: activeDateString });
        setSearchQuery('');
        setShowSearchRow(false);
    };

    // --- Styles ---
    const styles = {
        container: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflowX: 'auto',
            padding: '8px 16px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            whiteSpace: 'nowrap'
        },
        // content: {
        content: {
            flex: 1,
            overflowY: 'auto'
        },
        contentHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
        },
        title: {
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1f2937'
        },
        addButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#00D4B3',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
        },
        tableWrapper: {
            border: '1px solid #e5e7eb',
            borderRadius: '12px'
            // overflow: 'hidden' removed to allow dropdown to show
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            textAlign: 'left'
        },
        th: {
            padding: '12px 16px',
            backgroundColor: '#f9fafb',
            color: '#6b7280',
            fontWeight: '600',
            fontSize: '12px',
            textTransform: 'uppercase',
            borderBottom: '1px solid #e5e7eb'
        },
        td: {
            padding: '12px 16px',
            borderBottom: '1px solid #f3f4f6',
            color: '#374151'
        },
        input: {
            width: '100%',
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            outline: 'none',
            fontSize: '14px'
        },
        qtyControl: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
        },
        qtyBtn: {
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f3f4f6',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#4b5563'
        },
        checkbox: (checked) => ({
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: checked ? 'none' : '2px solid #d1d5db',
            backgroundColor: checked ? '#00D4B3' : 'white', // Teal
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            color: 'white',
            fontSize: '12px',
            margin: '0 auto'
        }),
        searchWrapper: {
            position: 'relative',
            width: '100%'
        },
        searchInput: {
            width: '100%',
            padding: '10px 12px 10px 36px',
            borderRadius: '8px',
            border: '1px solid #00D4B3', // Teal border
            outline: 'none',
            fontSize: '14px',
            boxShadow: '0 0 0 3px rgba(0, 212, 179, 0.1)'
        },
        resultsList: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            marginTop: '4px',
            maxHeight: '240px',
            overflowY: 'auto',
            zIndex: 50
        }
    };

    // Render Tabs
    // Render Tabs
    const renderTabs = () => {
        const tabs = [];
        // Default to at least 1 day if lengthOfStay is 0 or missing
        const totalDays = Math.max(1, lengthOfStay);

        for (let i = 1; i <= totalDays; i++) {
            tabs.push(
                <button
                    key={i}
                    onClick={() => setActiveDay(i)}
                    className={`day-tab-btn ${activeDay === i ? 'active' : ''}`}
                >
                    {t('receptionInpatients.details.dailyCare.day', { count: i, defaultValue: `Day ${i}` })}
                    <span className="tab-date">
                        {getDayDate(i)}
                    </span>
                </button>
            );
        }
        return tabs;
    };

    return (
        <div style={styles.container}>
            {/* Header / Tabs */}
            <div className="days-tabs-container no-scrollbar">
                {renderTabs()}
            </div>

            {dayLocked && (
                <div style={{ padding: '10px 16px', backgroundColor: '#FFF7ED', color: '#9A3412', fontSize: '13px', borderBottom: '1px solid #FDE68A' }}>
                    {t('receptionInpatients.details.treatmentPlan.pastDayLocked', { defaultValue: 'This day is in the past and cannot be edited.' })}
                </div>
            )}

            {/* Content Body */}
            <div style={styles.content} className="tab-content-inner">
                <div style={styles.contentHeader}>
                    <h3 style={styles.title}>
                        {t('receptionInpatients.details.treatmentPlan.titleFor', { date: activeDateString, defaultValue: 'Treatment Plan for {{date}}' })}
                    </h3>
                    {!isReadOnly && (
                        <button
                            onClick={() => setShowSearchRow(true)}
                            style={styles.addButton}
                            onMouseEnter={e => e.target.style.backgroundColor = '#00B89D'}
                            onMouseLeave={e => e.target.style.backgroundColor = '#00D4B3'}
                        >
                            <i className="fas fa-plus"></i> {t('receptionInpatients.details.services.add', { defaultValue: 'Add Service' })}
                        </button>
                    )}
                </div>

                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '50px', textAlign: 'center' }}>#</th>
                                <th style={styles.th}>{t('receptionInpatients.details.services.table.name', { defaultValue: 'Service Name' })}</th>
                                <th style={{ ...styles.th, width: '120px', textAlign: 'center' }}>{t('receptionInpatients.details.services.table.quantity', { defaultValue: 'Qty' })}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>{t('receptionInpatients.details.services.table.price', { defaultValue: 'Price' })}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>{t('receptionInpatients.details.services.table.total', { defaultValue: 'Total' })}</th>
                                <th style={{ ...styles.th, width: '60px', textAlign: 'center' }}>{t('receptionInpatients.details.services.table.fulfilled', { defaultValue: 'Done' })}</th>
                                <th style={styles.th}>{t('receptionInpatients.details.services.table.notes', { defaultValue: 'Additional Notes' })}</th>
                                {!isReadOnly && <th style={{ ...styles.th, width: '50px' }}></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Existing Rows */}
                            {dayServices.map((s, index) => (
                                <tr key={s.uniqueKey || `${s.id}-${index}`} style={{ backgroundColor: '#fff', transition: 'background-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                                    <td style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', fontFamily: 'monospace' }}>{index + 1}</td>
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: '500', color: '#111827' }}>{isRTL ? s.service_name_ar : s.service_name_en}</div>
                                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{s.service_code}</div>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <div style={styles.qtyControl}>
                                            <button
                                                style={{ ...styles.qtyBtn, opacity: (isReadOnly || (s.quantity || 1) <= 1) ? 0.5 : 1, cursor: (isReadOnly || (s.quantity || 1) <= 1) ? 'not-allowed' : 'pointer' }}
                                                onClick={() => !isReadOnly && onUpdateService(s.uniqueKey || s.id, { quantity: Math.max(1, (s.quantity || 1) - 1) })}
                                                disabled={isReadOnly || (s.quantity || 1) <= 1}
                                            >-</button>
                                            <input
                                                type="number"
                                                min="1"
                                                value={s.quantity}
                                                onChange={(e) => onUpdateService(s.uniqueKey || s.id, { quantity: parseInt(e.target.value) || 1 })}
                                                disabled={isReadOnly}
                                                style={{ ...styles.input, width: '50px', textAlign: 'center', padding: '4px' }}
                                                onFocus={e => e.target.style.borderColor = '#00D4B3'}
                                                onBlur={e => e.target.style.borderColor = '#d1d5db'}
                                            />
                                            <button
                                                style={styles.qtyBtn}
                                                onClick={() => !isReadOnly && onUpdateService(s.uniqueKey || s.id, { quantity: (s.quantity || 1) + 1 })}
                                                disabled={isReadOnly}
                                            >+</button>
                                        </div>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'monospace' }}>
                                        {parseFloat(s.price || 0).toFixed(2)}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>
                                        {(parseFloat(s.price || 0) * (s.quantity || 1)).toFixed(2)}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <div
                                            style={{ ...styles.checkbox(s.is_fulfilled), cursor: 'default' }}
                                        >
                                            {s.is_fulfilled && <i className="fas fa-check"></i>}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <input
                                            type="text"
                                            placeholder={t('receptionInpatients.details.services.placeholders.notes', { defaultValue: 'Notes...' })}
                                            value={s.notes || ''}
                                            onChange={(e) => !isReadOnly && onUpdateService(s.uniqueKey || s.id, { notes: e.target.value })}
                                            disabled={isReadOnly}
                                            style={{ ...styles.input, border: 'none', borderBottom: '1px solid transparent', padding: '4px 0', borderRadius: 0, backgroundColor: 'transparent' }}
                                            onFocus={e => e.target.style.borderBottom = '1px solid #00D4B3'}
                                            onBlur={e => e.target.style.borderBottom = '1px solid transparent'}
                                        />
                                    </td>
                                    {!isReadOnly && (
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            <button
                                                onClick={() => onRemoveService(s.uniqueKey || s.id)}
                                                style={{ color: '#9ca3af', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px' }}
                                                onMouseEnter={e => e.target.style.color = '#EF4444'}
                                                onMouseLeave={e => e.target.style.color = '#9ca3af'}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}

                            {/* Search Row */}
                            {showSearchRow && !isReadOnly && (
                                <tr style={{ backgroundColor: '#f0fdfa' }}>
                                    <td style={{ ...styles.td, textAlign: 'center', color: '#00D4B3' }}>
                                        <i className="fas fa-plus"></i>
                                    </td>
                                    <td style={{ ...styles.td, position: 'relative' }} colSpan="7">
                                        <div style={styles.searchWrapper}>
                                            <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
                                            <input
                                                autoFocus
                                                type="text"
                                                placeholder={t('receptionInpatients.details.services.search.placeholder', { defaultValue: 'Search services...' })}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') {
                                                        setShowSearchRow(false);
                                                        setSearchQuery('');
                                                    }
                                                }}
                                                style={styles.searchInput}
                                            />
                                            {/* Dropdown Results */}
                                            {searchQuery && (
                                                <div style={styles.resultsList}>
                                                    {filteredSearchOptions.map(service => (
                                                        <div
                                                            key={service.id}
                                                            onClick={() => handleSelectService(service)}
                                                            style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                                                        >
                                                            <div>
                                                                <div style={{ fontWeight: '500', color: '#1f2937' }}>{isRTL ? service.service_name_ar : service.service_name_en}</div>
                                                                <div style={{ fontSize: '12px', color: '#9ca3af' }}>{service.service_code}</div>
                                                            </div>
                                                            <div style={{ color: '#00D4B3', fontWeight: 'bold', fontSize: '13px' }}>
                                                                {parseFloat(service.price).toFixed(2)}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {filteredSearchOptions.length === 0 && (
                                                        <div style={{ padding: '12px 16px', color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
                                                            {t('receptionInpatients.details.services.search.empty', { defaultValue: 'No services found' })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {/* Empty State */}
                            {dayServices.length === 0 && !showSearchRow && (
                                <tr>
                                    <td colSpan="8" style={{ padding: '48px', textAlign: 'center', color: '#9ca3af' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                            <i className="fas fa-clipboard-list" style={{ fontSize: '32px', opacity: 0.2 }}></i>
                                            <p>{t('receptionInpatients.details.services.empty.day', { defaultValue: 'No services scheduled for this day' })}</p>
                                            {!readOnly && (
                                                <button onClick={() => setShowSearchRow(true)} style={{ color: '#00D4B3', fontWeight: '500', fontSize: '14px', border: 'none', background: 'none', cursor: 'pointer' }}>
                                                    {t('receptionInpatients.details.services.empty.addFirst', { defaultValue: 'Add First Service' })}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ServicesTab;
